import React, { useState } from 'react';
import { Card, Form, Button, Table, InputNumber, Input, DatePicker, App, Space } from 'antd';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '@/services/api.service';
import {
  LINE_SPECIFICATIONS_MAX_LENGTH,
  LineSpecificationsText,
} from '@/components/procurement/LineSpecifications';

export default function SupplierQuotationPortalPage() {
  const { token } = useParams<{ token: string }>();
  const { message } = App.useApp();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState<{ inquiry: any; supplier: any; lines: any[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form] = Form.useForm();

  React.useEffect(() => {
    if (!token) {
      setError('Invalid link');
      setLoading(false);
      return;
    }
    api
      .get(`/procurement/public/supplier-quotation/${token}`)
      .then((res) => {
        const d = res?.data?.data ?? res?.data;
        setData({ inquiry: d.inquiry, supplier: d.supplier, lines: d.lines ?? [] });
      })
      .catch(() => setError('Invalid or expired link'))
      .finally(() => setLoading(false));
  }, [token]);

  const onFinish = (values: any) => {
    if (!token || !data?.lines?.length) return;
    setSubmitting(true);
    const lines = data.lines.map((line: any, i: number) => ({
      supplier_inquiry_line_id: line.id,
      unit_price: values[`price_${line.id}`] ?? 0,
      quoted_quantity: values[`qty_${line.id}`] ?? line.quantity,
      uom: line.uom,
      lead_time_days: values[`lead_${line.id}`],
      promised_delivery_date: values[`delivery_${line.id}`]?.format?.('YYYY-MM-DD'),
      remarks: values[`remarks_${line.id}`],
      brand_offered: String(form.getFieldValue(`brand_${line.id}`) ?? '').trim() || null,
      compliance_notes: String(form.getFieldValue(`offered_spec_${line.id}`) ?? '').trim() || null,
    }));
    api
      .post(`/procurement/public/supplier-quotation/${token}/submit`, {
        quotation_date: values.quotation_date?.format?.('YYYY-MM-DD') || dayjs().format('YYYY-MM-DD'),
        validity_date: values.validity_date?.format?.('YYYY-MM-DD'),
        payment_terms: values.payment_terms,
        remarks: values.remarks,
        lines,
      })
      .then(() => {
        message.success('Quotation submitted successfully');
        setData(null);
        form.resetFields();
      })
      .catch((e) => message.error(e?.response?.data?.message || 'Submit failed'))
      .finally(() => setSubmitting(false));
  };

  if (loading) return <div style={{ padding: 48, textAlign: 'center' }}>Loading...</div>;
  if (error || !data) return <div style={{ padding: 48, textAlign: 'center' }}>{error || 'Not found'}</div>;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
      <Card title={`Quotation submission – ${data.supplier?.supplier_name ?? 'Supplier'}`}>
        <p><strong>Inquiry:</strong> {data.inquiry?.inquiry_number} – {data.inquiry?.inquiry_date ? dayjs(data.inquiry.inquiry_date).format('DD MMM YYYY') : ''}</p>
        <Form form={form} onFinish={onFinish} layout="vertical">
          <Form.Item name="quotation_date" label="Quotation Date" initialValue={dayjs()}>
            <DatePicker style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="validity_date" label="Validity Date">
            <DatePicker style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="payment_terms" label="Payment Terms">
            <Input placeholder="e.g. Net 30" />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
          <h4>Line items – enter your quote</h4>
          <Table
            size="small"
            rowKey="id"
            dataSource={data.lines}
            pagination={false}
            expandable={{
              defaultExpandAllRows: true,
              expandedRowRender: (r) => (
                <Space direction="vertical" style={{ width: '100%' }} size={4}>
                  <Form.Item name={`brand_${r.id}`} label="Brand / make offered" style={{ marginBottom: 4 }}>
                    <Input maxLength={255} style={{ maxWidth: 360 }} />
                  </Form.Item>
                  <Form.Item
                    name={`offered_spec_${r.id}`}
                    label="Offered specification / deviations from the requested specification"
                    style={{ marginBottom: 4 }}
                  >
                    <Input.TextArea
                      autoSize={{ minRows: 2, maxRows: 8 }}
                      maxLength={LINE_SPECIFICATIONS_MAX_LENGTH}
                      showCount
                      placeholder="Leave blank if you comply fully with the requested specification"
                    />
                  </Form.Item>
                </Space>
              ),
            }}
            columns={[
              { title: 'Item', dataIndex: 'item_code', width: 100 },
              {
                title: 'Description',
                dataIndex: 'item_description',
                width: 280,
                render: (value, r) => (
                  <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {value || '—'}
                    <LineSpecificationsText value={r.specifications} label="Required spec" />
                  </div>
                ),
              },
              { title: 'Qty requested', dataIndex: 'quantity', width: 100, align: 'right' },
              { title: 'UOM', dataIndex: 'uom', width: 60 },
              {
                title: 'Your qty',
                width: 100,
                render: (_, r) => (
                  <Form.Item name={`qty_${r.id}`} noStyle initialValue={r.quantity}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                ),
              },
              {
                title: 'Unit price',
                width: 120,
                render: (_, r) => (
                  <Form.Item name={`price_${r.id}`} rules={[{ required: true }]} noStyle>
                    <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
                  </Form.Item>
                ),
              },
              {
                title: 'Lead time (days)',
                width: 110,
                render: (_, r) => (
                  <Form.Item name={`lead_${r.id}`} noStyle>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                ),
              },
              {
                title: 'Delivery date',
                width: 140,
                render: (_, r) => (
                  <Form.Item name={`delivery_${r.id}`} noStyle>
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                ),
              },
            ]}
          />
          <Form.Item style={{ marginTop: 16 }}>
            <Button type="primary" htmlType="submit" loading={submitting}>Submit quotation</Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
